import {
  Delete,
  Link as LinkIcon,
  ContentCopy,
  OpenInNew,
  Download,
  Rule,
  Description,
  ExitToApp,
  ListAlt,
  Subject,
  PlaylistRemove,
  HighlightOff,
} from '@mui/icons-material'
import {
  Menu,
  MenuItem,
  MenuProps,
  ListItemIcon,
  ListItemText,
  Link,
  Divider,
} from '@mui/material'
import { useRunAllRulesWithDocChange } from '@/hooks/doc-changes-wrapper-hooks'
import useLocationHelpers from '@/hooks/useLocationHelpers'
import useOverlay, { OverlayState } from '@/hooks/useOverlay'
import useRelevantWorkflowState from '@/hooks/useRelevantWorkflowState'
import {
  useCancelEvents,
  useFailEvent,
  useTriggerWorkflowStateEvent,
  useWorkflowStateEventStatuses,
} from '@/service-library/hooks/events'
import { useGetWorkflowStateEvents } from '@/service-library/hooks/workflow-state-events'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import { Document } from '@/types/documents'
import { WorkflowStateEvent } from '@/types/workflow-state-events'
import { handleDownloadOriginalDocument } from '@/utils/download-file'
import {
  showErrorSnackbar,
  showInfoSnackbar,
  showSuccessSnackbar,
} from '@/utils/snackbars'
import DocumentDialog from '@/components/document-dialog/DocumentDialog'
import ExportJobsDialog from '@/components/export-jobs/ExportJobsDialog'
import ClearAllChipsDialog from '@/components/validation/ClearAllChipsDialog'
import { useDocumentsChangeSetsContext } from '@/components/validation/providers/DocumentsChangeSetsProvider'
import WorkflowStateDescriptionDialog from '@/components/workflow-state-page/WorkflowStateDescriptionDialog'
import DeleteDocumentsDialog from '@/components/workflows/DeleteDocumentsDialog'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import copyToClipboard from './copy-to-clipboard'

type DocumentActionsMenuProps = {
  /** Overlay state. */
  overlay: OverlayState

  /** Target document. */
  document: Document

  /** ID the project belongs to. */
  projectId: string

  displayedOptions?: (
    | 'open-document-dialog'
    | 'view-workflow-state-documentation'
    | 're-run-rules'
    | 'view-export-jobs'
    | 'clear-events'
    | 'clear-all-chips'
    | 'cancel-events'
  )[]

  /** Callback that runs before the document is deleted. */
  onBeforeDelete?: () => void

  /** Callback that runs after the document is successfully deleted. */
  onDelete?: () => void
} & Partial<MenuProps>

export default function DocumentActionsMenu({
  overlay,
  document,
  displayedOptions = [],
  projectId,
  onBeforeDelete,
  onDelete = () => {},
  ...rest
}: DocumentActionsMenuProps) {
  const { getCurrentPathWithParams } = useLocationHelpers()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const getRelevantWorkflowState = useRelevantWorkflowState()
  const relevantWorkflowState = getRelevantWorkflowState(document)
  const documentsChangeSetsContextData = useDocumentsChangeSetsContext()
  const isSuperUser = useIsSuperUser()

  const documentOverlay = useOverlay()
  const documentationOverlay = useOverlay()
  const deleteDocsOverlay = useOverlay()
  const clearAllChipsOverlay = useOverlay()
  const exportJobsOverlay = useOverlay()

  const { eventStatuses = [] } = useWorkflowStateEventStatuses({
    filters: {
      document_id: document.id,
    },
    enabled: isSuperUser,
  })
  const { failEvent } = useFailEvent()

  const { workflowStateEvents } = useGetWorkflowStateEvents({
    filters: {
      workflow_state_id: relevantWorkflowState?.id || '',
      code__in: 'on_enter,on_exit',
    },
  })

  const { runRules } = useRunAllRulesWithDocChange({
    documentIds: [document.id],
    workflowId: selectedWorkflow?.id || '',
    workflowStateId: relevantWorkflowState?.id || '',
    documentsChangeSetsContextData,
    onError: () => {
      showErrorSnackbar('Document validation failed. Please try again later.')
    },
  })

  const { triggerWorkflowStateEvent } = useTriggerWorkflowStateEvent()
  const { cancelEvents } = useCancelEvents({
    onError: () => {
      showErrorSnackbar('Failed to cancel events. Please try again later.')
    },
  })

  const currentPath = getCurrentPathWithParams()

  const pathToDocument = `${currentPath}${
    currentPath.includes('document_id')
      ? ''
      : currentPath.includes('?')
      ? `&document_id=${document.id}`
      : `?document_id=${document.id}`
  }`

  function handleDeleteDocument() {
    deleteDocsOverlay.open()
    overlay.close()
  }

  function handleReRunRules(forceClearCache = false) {
    showInfoSnackbar('Validating Document')
    runRules(forceClearCache)
    overlay.close()
  }

  function triggerEvent(eventCode: string) {
    const event = workflowStateEvents.find(
      (event) => event.code === eventCode,
    ) as WorkflowStateEvent
    triggerWorkflowStateEvent([
      {
        workflow_state_event_id: event.id,
        document_id: document.id,
        status: 'waiting',
      },
    ])
    overlay.close()
  }

  return (
    <>
      <Menu
        open={overlay.isOpen}
        onClose={overlay.close}
        anchorEl={overlay.anchorEl as Element}
        disableAutoFocusItem
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        {...rest}
      >
        {displayedOptions.includes('open-document-dialog') && (
          <MenuItem
            onClick={() => {
              documentOverlay.open()
              overlay.close()
            }}
          >
            <ListItemIcon>
              <Subject />
            </ListItemIcon>
            <ListItemText>Details</ListItemText>
          </MenuItem>
        )}

        {relevantWorkflowState &&
          displayedOptions.includes('view-workflow-state-documentation') && (
            <MenuItem
              onClick={() => {
                documentationOverlay.open()
                overlay.close()
              }}
            >
              <ListItemIcon>
                <Description />
              </ListItemIcon>
              <ListItemText>Workflow State Documentation</ListItemText>
            </MenuItem>
          )}

        <MenuItem
          onClick={() => {
            copyToClipboard(`${window.location.host}${pathToDocument}`)
            showInfoSnackbar('Document link copied to clipboard')
            overlay.close()
          }}
        >
          <ListItemIcon>
            <LinkIcon />
          </ListItemIcon>
          <ListItemText>Copy Link</ListItemText>
        </MenuItem>

        <MenuItem
          component={Link}
          target="_blank"
          href={pathToDocument}
          onClick={overlay.close}
        >
          <ListItemIcon>
            <OpenInNew />
          </ListItemIcon>
          <ListItemText>Open in New Tab</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleDownloadOriginalDocument(document.id)
            overlay.close()
          }}
        >
          <ListItemIcon>
            <Download />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            copyToClipboard(document.id)
            showInfoSnackbar('Document ID copied to clipboard')
            overlay.close()
          }}
        >
          <ListItemIcon>
            <ContentCopy />
          </ListItemIcon>
          <ListItemText>Copy ID</ListItemText>
        </MenuItem>

        {displayedOptions.includes('re-run-rules') && (
          <MenuItem
            onClick={() => {
              handleReRunRules()
            }}
          >
            <ListItemIcon>
              <Rule />
            </ListItemIcon>
            <ListItemText>Re-Run Rules</ListItemText>
          </MenuItem>
        )}

        {displayedOptions.includes('cancel-events') && (
          <MenuItem
            onClick={() => {
              cancelEvents({ id: document.id })
              showInfoSnackbar('Canceling Events...')
              overlay.close()
            }}
          >
            <ListItemIcon>
              <HighlightOff />
            </ListItemIcon>
            <ListItemText>Cancel Events</ListItemText>
          </MenuItem>
        )}

        {displayedOptions.includes('clear-all-chips') && (
          <MenuItem
            onClick={() => {
              clearAllChipsOverlay.open()
              overlay.close()
            }}
          >
            <ListItemIcon>
              <PlaylistRemove />
            </ListItemIcon>
            <ListItemText>Clear All Chips</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            handleDeleteDocument()
          }}
        >
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>

        {isSuperUser && (
          <>
            {relevantWorkflowState && (
              <>
                <Divider />
                <MenuItem
                  onClick={() => {
                    handleReRunRules(true)
                  }}
                  sx={{ color: SUPER_USER_ONLY_COLOR }}
                >
                  <ListItemIcon>
                    <Rule
                      sx={{
                        color: SUPER_USER_ONLY_COLOR,
                      }}
                    />
                  </ListItemIcon>
                  Re-Run Rules (Clear Cache)
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    triggerEvent('on_enter')
                  }}
                  sx={{ color: SUPER_USER_ONLY_COLOR }}
                >
                  <ListItemIcon>
                    <ExitToApp
                      sx={{
                        color: SUPER_USER_ONLY_COLOR,
                      }}
                    />
                  </ListItemIcon>
                  Run On Enter Event
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    triggerEvent('on_exit')
                  }}
                  sx={{ color: SUPER_USER_ONLY_COLOR }}
                >
                  <ListItemIcon>
                    <ExitToApp
                      sx={{
                        color: SUPER_USER_ONLY_COLOR,
                        transform: 'rotate(180deg)',
                      }}
                    />
                  </ListItemIcon>
                  Run On Exit Event
                </MenuItem>
              </>
            )}

            {displayedOptions.includes('view-export-jobs') && (
              <MenuItem
                key="export-jobs"
                sx={{ color: SUPER_USER_ONLY_COLOR }}
                onClick={() => {
                  exportJobsOverlay.open()
                  overlay.close()
                }}
              >
                <ListItemIcon sx={{ color: SUPER_USER_ONLY_COLOR }}>
                  <ListAlt />
                </ListItemIcon>
                <ListItemText>View Export Jobs</ListItemText>
              </MenuItem>
            )}

            {displayedOptions.includes('clear-events') && (
              <MenuItem
                key="clear-events"
                sx={{ color: SUPER_USER_ONLY_COLOR }}
                onClick={() => {
                  eventStatuses.forEach((eventStatus) => {
                    if (eventStatus.status === 'running_async') {
                      failEvent({ id: eventStatus.id })
                    }
                  })
                  overlay.close()
                }}
              >
                <ListItemIcon sx={{ color: SUPER_USER_ONLY_COLOR }}>
                  <Delete />
                </ListItemIcon>
                <ListItemText>Clear Events</ListItemText>
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {displayedOptions.includes('open-document-dialog') && (
        <DocumentDialog
          overlay={documentOverlay}
          documentId={document.id}
          projectId={projectId}
          onDelete={onDelete}
        />
      )}

      {relevantWorkflowState && (
        <WorkflowStateDescriptionDialog
          overlay={documentationOverlay}
          projectId={projectId}
          workflowState={relevantWorkflowState}
        />
      )}

      <DeleteDocumentsDialog
        overlay={deleteDocsOverlay}
        documentIds={[document.id]}
        onDeleteError={() => {
          showErrorSnackbar(
            'Failed to delete document. Please try again later.',
          )
        }}
        onDeleteSuccess={() => {
          showSuccessSnackbar('Document Deleted')
          onDelete?.()
          onBeforeDelete?.()
        }}
      />

      {displayedOptions.includes('view-export-jobs') && (
        <ExportJobsDialog overlay={exportJobsOverlay} document={document} />
      )}

      {displayedOptions.includes('clear-all-chips') && (
        <ClearAllChipsDialog
          document={document}
          overlay={clearAllChipsOverlay}
        />
      )}
    </>
  )
}
